import { useEffect, useState, type ReactNode } from "react";
import "./Layout.css";

interface MeResponse {
  client: { client_id: string; owner_email: string | null } | null;
  is_operator?: boolean;
}

interface LayoutProps {
  children: ReactNode;
  // Optional override: when a page already knows the auth state (e.g. login/setup),
  // pass clientId={null} to suppress the nav while we ignore /api/me.
  suppressAuthFetch?: boolean;
  // Legacy prop kept for the few callers that still pass it; unused otherwise.
  clientId?: string | null;
}

async function signOut() {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    /* ignore */
  }
  window.location.href = "/login";
}

export function Layout({ children, suppressAuthFetch }: LayoutProps) {
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    if (suppressAuthFetch) return;
    fetch("/api/me", { credentials: "include" })
      .then((r) => r.json())
      .then(setMe)
      .catch(() => setMe({ client: null }));
  }, [suppressAuthFetch]);

  const signedIn = !!me?.client;
  const isOperator = !!me?.is_operator;

  return (
    <div className="layout">
      <header className="layout__header">
        <div className="layout__inner">
          <a href="/" className="layout__brand">
            <img src="/retrofit-ai-icon.svg" alt="" className="layout__brand-mark" />
            <span className="layout__brand-name">Retrofit AI Monitor</span>
          </a>
          {signedIn && (
            <nav className="layout__nav">
              <a href="/dashboard">Dashboard</a>
              <a href="/dashboard/queue">Queue</a>
              <a href="/dashboard/archive">Archive</a>
              <a href="/dashboard/reallocation">Reallocation</a>
              <a href="/settings">Notifications</a>
              <a href="/meta/start">Connect Meta</a>
              {isOperator && <a href="/admin">Admin</a>}
              <button type="button" className="layout__signout" onClick={signOut}>
                Sign out
              </button>
            </nav>
          )}
          {!signedIn && me && (
            <nav className="layout__nav">
              <a href="/login">Sign in</a>
            </nav>
          )}
        </div>
      </header>
      <main className="layout__main">
        <div className="layout__inner">{children}</div>
      </main>
      <footer className="layout__footer">
        <div className="layout__inner">
          <small>&copy; Retrofit AI Systems</small>
          <nav>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
